import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import RelatedPages from '../components/RelatedPages';

/* ── Datos ──────────────────────────────────────────────────────────────── */

const AGENTS = [
  {
    id: 'comercial',
    label: 'Comercial',
    color: '#3B82F6',
    angle: 0,
    tools: ['CRM', 'WhatsApp', 'Email'],
    role: 'Califica leads, puntúa intención de compra y abre conversaciones.',
    autonomy: 'green',
  },
  {
    id: 'cobro',
    label: 'Cobro',
    color: '#10B981',
    angle: 60,
    tools: ['Stripe', 'Contratos', 'Facturas'],
    role: 'Genera propuestas, gestiona pagos y detecta deuda vencida.',
    autonomy: 'amber',
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    color: '#8B5CF6',
    angle: 120,
    tools: ['Usuarios', 'Publer', 'Slack'],
    role: 'Crea la cuenta del cliente y conecta sus herramientas tras el pago.',
    autonomy: 'green',
  },
  {
    id: 'operaciones',
    label: 'Operaciones',
    color: '#F59E0B',
    angle: 180,
    tools: ['Calendar', 'Tareas', 'Integraciones'],
    role: 'Coordina agendas, asigna recursos y monitoriza SLAs.',
    autonomy: 'amber',
  },
  {
    id: 'soporte',
    label: 'Soporte',
    color: '#06B6D4',
    angle: 240,
    tools: ['FAQ', 'Tickets', 'Escalado'],
    role: 'Resuelve dudas, escala incidencias y actualiza el estado del cliente.',
    autonomy: 'green',
  },
  {
    id: 'seguridad',
    label: 'Seguridad',
    color: '#EF4444',
    angle: 300,
    tools: ['Auditoría', 'Logs', 'Permisos'],
    role: 'Audita cada acción, revoca permisos y genera trazabilidad completa.',
    autonomy: 'red',
  },
];

const FLOW_STEPS = [
  {
    n: '01',
    actor: 'Sistema',
    title: 'Lead entra al CRM',
    desc: 'Un contacto llega vía web, WhatsApp o campaña. Se registra automáticamente con origen, hora y canal.',
    human: false,
    color: '#3B82F6',
  },
  {
    n: '02',
    actor: 'Agente Comercial',
    title: 'Califica y puntúa',
    desc: 'Analiza comportamiento, sector y urgencia. Asigna un score de 0-100 y propone el siguiente mensaje de seguimiento.',
    human: false,
    color: '#3B82F6',
  },
  {
    n: '03',
    actor: 'Aynara',
    title: 'Verifica riesgo y aprueba',
    desc: 'Orquestadora revisa el perfil consolidado. Si el lead supera el umbral de riesgo, solicita revisión humana antes de continuar.',
    human: true,
    color: '#F59E0B',
  },
  {
    n: '04',
    actor: 'Agente de Cobro',
    title: 'Genera contrato y enlace Stripe',
    desc: 'Prepara el contrato según el plan seleccionado, genera el enlace de pago y lo envía por WhatsApp.',
    human: true,
    color: '#10B981',
  },
  {
    n: '05',
    actor: 'Agente de Onboarding',
    title: 'Crea usuario y conecta herramientas',
    desc: 'Tras confirmar el pago, activa el acceso, asigna permisos, conecta Publer y envía el mensaje de bienvenida.',
    human: false,
    color: '#8B5CF6',
  },
  {
    n: '06',
    actor: 'Agente de Seguridad',
    title: 'Audita y cierra el ciclo',
    desc: 'Registra cada acción, valida que los permisos son mínimos necesarios y archiva el trazado completo del caso.',
    human: false,
    color: '#EF4444',
  },
];

const AUTONOMY_LEGEND = [
  { color: '#22C55E', label: 'Acción autónoma', dot: 'bg-green-500' },
  { color: '#F59E0B', label: 'Requiere aprobación humana', dot: 'bg-amber-500' },
  { color: '#EF4444', label: 'Bloqueado por política', dot: 'bg-red-500' },
];

const fade = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };

/* ── Diagrama SVG de agentes ─────────────────────────────────────────────── */
function AgentDiagram() {
  const [active, setActive] = useState(null);
  const R = 148;   // radio del círculo de agentes
  const W = 560;
  const H = 560;
  const cx = W / 2;
  const cy = H / 2;

  const AUTONOMY_CLR = { green: '#22C55E', amber: '#F59E0B', red: '#EF4444' };

  return (
    <div className="flex flex-col items-center gap-6" data-testid="agent-diagram">
      {/* SVG */}
      <div className="relative w-full max-w-[560px] mx-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ filter: 'drop-shadow(0 0 32px rgba(59,130,246,0.12))' }}>
          {/* Líneas de comunicación */}
          {AGENTS.map(ag => {
            const rad = (ag.angle - 90) * (Math.PI / 180);
            const x = cx + R * Math.cos(rad);
            const y = cy + R * Math.sin(rad);
            return (
              <line
                key={ag.id}
                x1={cx} y1={cy} x2={x} y2={y}
                stroke={active === ag.id ? ag.color : 'rgba(148,163,184,0.18)'}
                strokeWidth={active === ag.id ? 2 : 1}
                strokeDasharray={active === ag.id ? '' : '4 4'}
                style={{ transition: 'all 0.25s' }}
              />
            );
          })}

          {/* Nodo central — Aynara */}
          <circle cx={cx} cy={cy} r={54} fill="rgba(59,130,246,0.12)" stroke="rgba(59,130,246,0.45)" strokeWidth={1.5} />
          <circle cx={cx} cy={cy} r={40} fill="rgba(59,130,246,0.22)" />
          <text x={cx} y={cy - 6} textAnchor="middle" fill="#F8FAFC" fontSize={13} fontWeight={700} fontFamily="Outfit,sans-serif">Aynara</text>
          <text x={cx} y={cy + 10} textAnchor="middle" fill="#94A3B8" fontSize={9} fontFamily="IBM Plex Sans,sans-serif">Orquestadora</text>

          {/* Nodos de agentes */}
          {AGENTS.map(ag => {
            const rad = (ag.angle - 90) * (Math.PI / 180);
            const x = cx + R * Math.cos(rad);
            const y = cy + R * Math.sin(rad);
            const isActive = active === ag.id;

            return (
              <g
                key={ag.id}
                style={{ cursor: 'pointer' }}
                onClick={() => setActive(isActive ? null : ag.id)}
              >
                <circle cx={x} cy={y} r={isActive ? 36 : 32} fill={`${ag.color}22`}
                  stroke={isActive ? ag.color : `${ag.color}66`} strokeWidth={isActive ? 1.8 : 1}
                  style={{ transition: 'all 0.2s' }} />
                {/* indicador de autonomía */}
                <circle cx={x + 22} cy={y - 22} r={6}
                  fill={AUTONOMY_CLR[ag.autonomy]}
                  stroke="rgba(3,7,18,0.8)" strokeWidth={1.5} />
                <text x={x} y={y - 4} textAnchor="middle" fill="#F8FAFC" fontSize={10} fontWeight={600} fontFamily="Outfit,sans-serif">{ag.label}</text>
                <text x={x} y={y + 9} textAnchor="middle" fill="#94A3B8" fontSize={8} fontFamily="IBM Plex Sans,sans-serif">
                  {ag.tools.slice(0, 2).join(' · ')}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Tooltip agente activo */}
      {active && (() => {
        const ag = AGENTS.find(a => a.id === active);
        return (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md rounded-2xl border p-4 text-sm"
            style={{ background: `${ag.color}11`, borderColor: `${ag.color}33` }}
          >
            <p className="font-semibold text-text-heading mb-1">{ag.label}</p>
            <p className="text-text-body text-xs leading-relaxed">{ag.role}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {ag.tools.map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border"
                  style={{ borderColor: `${ag.color}44`, color: ag.color }}>
                  {t}
                </span>
              ))}
            </div>
          </motion.div>
        );
      })()}

      {/* Leyenda */}
      <div className="flex flex-wrap justify-center gap-5 mt-2">
        {AUTONOMY_LEGEND.map(l => (
          <div key={l.label} className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${l.dot}`} />
            <span className="text-xs text-text-muted">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Página principal ────────────────────────────────────────────────────── */
export default function SistemaMultiagenteCorePage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Sistema Multiagente | Neuralia369"
        description="No vendemos agentes sueltos. Orquestamos equipos de agentes que colaboran, se supervisan y rinden cuentas. Aynara decide quién actúa, cuándo y con qué permisos."
        path="/sistema-multiagente"
      />

      <main className="min-h-screen bg-background" data-testid="sistema-multiagente-page">

        {/* ── HERO ──────────────────────────────────────────────────────── */}
        <section className="relative px-4 pt-28 pb-24 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
          {/* Glow de fondo */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full"
              style={{ background: 'radial-gradient(ellipse at center, rgba(59,130,246,0.10) 0%, transparent 70%)' }} />
          </div>

          <div className="relative mx-auto max-w-4xl">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.p variants={fade} className="editorial-kicker mb-6">
                Sistema multiagente
              </motion.p>

              <motion.h1
                variants={fade}
                className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-text-heading leading-[1.08] tracking-tight"
                data-testid="hero-title"
              >
                No vendemos agentes sueltos. Orquestamos equipos de agentes que colaboran, se supervisan y rinden cuentas.
              </motion.h1>

              <motion.p
                variants={fade}
                className="mt-6 text-base sm:text-lg text-text-body max-w-2xl leading-relaxed"
                data-testid="hero-subtitle"
              >
                Aynara decide quién actúa, cuándo y con qué permisos. Cada agente tiene su rol, sus herramientas autorizadas y sus puntos de control humano.
              </motion.p>

              <motion.div variants={fade} className="mt-10 flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/activar')}
                  className="btn-primary px-8 py-3.5 text-base"
                  data-testid="hero-cta"
                >
                  Solicitar activación
                </button>
                <button
                  onClick={() => document.getElementById('diagrama')?.scrollIntoView({ behavior: 'smooth' })}
                  className="btn-secondary px-8 py-3.5 text-base"
                  data-testid="hero-diagram-link"
                >
                  Ver el diagrama
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── DEFINICIÓN ─────────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="definition-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="grid lg:grid-cols-2 gap-12 items-start">

              <div className="space-y-6">
                <motion.p variants={fade} className="editorial-kicker">Qué es un sistema multiagente</motion.p>
                <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading">
                  Agentes especializados que colaboran bajo un orquestador único.
                </motion.h2>
                <motion.p variants={fade} className="text-text-body text-sm sm:text-base leading-relaxed">
                  Un sistema multiagente no es un único modelo de IA que hace todo. Es un conjunto de agentes especializados —cada uno con un rol, un conjunto de herramientas autorizadas y límites claros— que colaboran bajo la coordinación de un orquestador central.
                </motion.p>
                <motion.p variants={fade} className="text-text-body text-sm sm:text-base leading-relaxed">
                  Los agentes comparten memoria estructurada, se pasan contexto de forma controlada y elevan las decisiones sensibles a supervisión humana antes de ejecutarlas. El resultado es un sistema que opera con autonomía donde puede, y con prudencia donde debe.
                </motion.p>
              </div>

              <motion.div variants={stagger} className="space-y-4">
                {[
                  {
                    icon: '⚡',
                    title: 'Superior a la automatización por reglas',
                    desc: 'Los sistemas basados en reglas se rompen ante la variabilidad del mundo real. Los agentes razonan sobre el contexto, adaptan su respuesta y siguen aprendiendo del historial.',
                  },
                  {
                    icon: '🔐',
                    title: 'Permisos granulares por agente',
                    desc: 'Cada agente solo puede acceder a las herramientas y datos estrictamente necesarios para su función. Ningún agente tiene acceso total al sistema.',
                  },
                  {
                    icon: '👁️',
                    title: 'Supervisión humana en acciones sensibles',
                    desc: 'Contratos, cobros y cambios de acceso requieren aprobación explícita. El sistema no actúa en zonas críticas sin una confirmación documentada.',
                  },
                  {
                    icon: '🧠',
                    title: 'Memoria compartida y coherente',
                    desc: 'Todos los agentes leen del mismo estado del cliente. No hay silos de información ni versiones contradictorias entre equipos.',
                  },
                ].map((item, i) => (
                  <motion.div key={i} variants={fade}
                    className="glass-card p-5 flex gap-4 items-start"
                    data-testid={`definition-card-${i}`}>
                    <span className="text-xl flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div>
                      <p className="text-sm font-semibold text-text-heading mb-1">{item.title}</p>
                      <p className="text-xs text-text-muted leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}

                {/* Dato Gartner */}
                <motion.div variants={fade}
                  className="rounded-2xl border border-primary/25 bg-primary/8 p-5"
                  data-testid="gartner-stat">
                  <p className="text-xs text-primary uppercase tracking-widest mb-2 font-medium">Proyección de industria</p>
                  <p className="text-text-heading text-sm sm:text-base font-semibold leading-relaxed">
                    "Gartner proyecta que el 70% de los sistemas multiagente tendrán agentes con roles especializados para 2027."
                  </p>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── DIAGRAMA ─────────────────────────────────────────────────────── */}
        <section id="diagrama" className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="diagram-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="flex flex-col items-center text-center mb-14">
              <motion.p variants={fade} className="editorial-kicker mb-4">Arquitectura de agentes</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading max-w-xl">
                Aynara en el centro. Seis agentes especializados con roles y herramientas definidas.
              </motion.h2>
              <motion.p variants={fade} className="mt-3 text-xs sm:text-sm text-text-muted max-w-md">
                Haz clic en cualquier agente para ver su rol y herramientas autorizadas.
              </motion.p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <AgentDiagram />
            </motion.div>
          </div>
        </section>

        {/* ── FLUJO END-TO-END ─────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="flow-section">
          <div className="mx-auto max-w-7xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="mb-14">
              <motion.p variants={fade} className="editorial-kicker mb-4">Caso práctico</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading max-w-2xl">
                De lead a cliente activo: seis pasos coordinados entre agentes.
              </motion.h2>
              <motion.p variants={fade} className="mt-3 text-sm text-text-muted max-w-xl">
                Los puntos marcados en ámbar requieren confirmación humana antes de que el siguiente agente actúe.
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="relative">

              {/* Línea vertical conectora */}
              <div className="absolute left-6 sm:left-8 top-8 bottom-8 w-px bg-border-subtle hidden sm:block" />

              <div className="space-y-4">
                {FLOW_STEPS.map((step, i) => (
                  <motion.div key={i} variants={fade}
                    className={`relative flex gap-5 sm:gap-8 rounded-2xl border p-5 sm:p-6 transition-all ${
                      step.human
                        ? 'border-amber-500/25 bg-amber-500/5'
                        : 'border-border-subtle bg-card/40'
                    }`}
                    data-testid={`flow-step-${i}`}>

                    {/* Número */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold font-mono"
                        style={{ background: `${step.color}18`, color: step.color, border: `1px solid ${step.color}33` }}>
                        {step.n}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border"
                          style={{ color: step.color, borderColor: `${step.color}44`, background: `${step.color}10` }}>
                          {step.actor}
                        </span>
                        {step.human && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400"
                            data-testid={`human-checkpoint-${i}`}>
                            ⚠ Control humano
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-text-heading mb-1">{step.title}</p>
                      <p className="text-xs text-text-muted leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── DIFERENCIADOR ───────────────────────────────────────────────── */}
        <section className="px-4 py-20 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="differentiator-section">
          <div className="mx-auto max-w-4xl">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
              className="glass-card tracing-panel p-8 sm:p-12 text-center space-y-6">
              <motion.p variants={fade} className="editorial-kicker">Por qué Neuralia369</motion.p>
              <motion.blockquote variants={fade}
                className="text-base sm:text-lg lg:text-xl font-heading font-semibold text-text-heading leading-snug"
                data-testid="differentiator-quote">
                "Mientras otros venden un chatbot con IA, nosotros vendemos un sistema operativo con agentes especializados que se coordinan, se auditan y se pueden revocar en cualquier momento."
              </motion.blockquote>
              <motion.div variants={stagger} className="flex flex-wrap justify-center gap-3 pt-2">
                {[
                  'Roles separados por agente',
                  'Permisos mínimos necesarios',
                  'Auditoría completa de acciones',
                  'Revocación en tiempo real',
                  'Control humano en puntos críticos',
                ].map(tag => (
                  <motion.span key={tag} variants={fade}
                    className="text-xs px-3 py-1.5 rounded-full border border-primary/25 bg-primary/8 text-text-body"
                    data-testid={`differentiator-tag-${tag.toLowerCase().replace(/\s/g,'-')}`}>
                    {tag}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ── CTA ─────────────────────────────────────────────────────────── */}
        <section className="px-4 py-24 sm:px-6 lg:px-8 border-t border-border-subtle" data-testid="cta-section">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="space-y-6">
              <motion.p variants={fade} className="editorial-kicker">Siguiente paso</motion.p>
              <motion.h2 variants={fade} className="text-base sm:text-lg font-heading font-semibold text-text-heading">
                ¿Tu negocio está listo para operar con un equipo de agentes?
              </motion.h2>
              <motion.p variants={fade} className="text-sm text-text-muted max-w-md mx-auto">
                Evaluamos tu operación, definimos los agentes necesarios y activamos el sistema en tu entorno. Sin código adicional.
              </motion.p>
              <motion.div variants={fade} className="flex flex-col sm:flex-row justify-center gap-4 pt-2">
                <button
                  onClick={() => navigate('/activar')}
                  className="btn-primary px-10 py-4 text-base"
                  data-testid="cta-main-btn"
                >
                  Solicitar activación
                </button>
                <button
                  onClick={() => navigate('/comparativa')}
                  className="btn-secondary px-10 py-4 text-base"
                  data-testid="cta-secondary-btn"
                >
                  Ver comparativa
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </main>
      <RelatedPages pages={['seguridad-agentes','soluciones','developers','recursos']} />
    </>
  );
}
